import React, { useEffect, useState } from 'react';
import { getTutors } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PaymentModal from '../components/PaymentModal';
import toast from 'react-hot-toast';
import { FiSearch, FiMapPin, FiPhone, FiLock, FiFilter, FiStar } from 'react-icons/fi';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [tutors, setTutors]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ city: '', subject: '' });
  const [payTarget, setPayTarget] = useState(null);

  const fetchTutors = async () => {
    setLoading(true);
    try { const { data } = await getTutors(filters); setTutors(data.tutors); }
    catch { toast.error('Failed to load tutors'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTutors(); }, []);

  const unlockedIds = user?.unlockedContacts?.map(String) || [];

  const modeColors = { online: '#EEF2FF', offline: '#FFF7ED', both: '#ECFDF5' };
  const modeTextColors = { online: 'var(--primary)', offline: '#D97706', both: 'var(--success)' };

  return (
    <div style={{ background: 'var(--lighter)', minHeight: '100vh', padding: '32px 0' }}>
      <div className="container-fluid px-4 px-lg-5">
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, marginBottom: '4px' }}>
            Find Your Tutor 🎯
          </h1>
          <p style={{ color: 'var(--gray)', fontSize: '15px', marginBottom: 0 }}>
            {user?.studentProfile?.class} · {user?.studentProfile?.board} · {user?.city}
          </p>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          {[
            { icon: '🔓', label: 'Unlocked', value: unlockedIds.length, color: '#EEF2FF' },
            { icon: '🎓', label: 'Class', value: user?.studentProfile?.class || '—', color: '#ECFDF5' },
            { icon: '📋', label: 'Board', value: user?.studentProfile?.board || '—', color: '#FFF7ED' },
            { icon: '💰', label: 'Budget/hr', value: user?.studentProfile?.budget ? `₹${user.studentProfile.budget}` : '—', color: '#FEF2F2' },
          ].map(s => (
            <div key={s.label} className="col-6 col-md-3">
              <div style={{ background: 'white', borderRadius: '20px', padding: '20px 24px', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: '18px', fontFamily: 'var(--font-display)', fontWeight: 800, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '3px' }}>{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '20px', boxShadow: 'var(--shadow-sm)', marginBottom: '24px' }}>
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '6px', display: 'block' }}>SEARCH BY CITY</label>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray)' }} />
                <input placeholder="City, area..." value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)' }} />
              </div>
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '6px', display: 'block' }}>SUBJECT</label>
              <select value={filters.subject} onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)' }}>
                <option value="">All Subjects</option>
                {['Maths','Physics','Chemistry','Biology','English','Computer Science','Hindi'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <button onClick={fetchTutors} className="btn-primary-custom" style={{ width: '100%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FiFilter size={14} /> Search
              </button>
            </div>
          </div>
        </div>

        {/* Tutor cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="spinner-border" style={{ color: 'var(--primary)', width: '40px', height: '40px' }} />
          </div>
        ) : (
          <div className="row g-3">
            {tutors.length === 0 && (
              <div className="col-12" style={{ textAlign: 'center', padding: '60px', color: 'var(--gray)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                <p>No tutors found. Try adjusting your filters.</p>
              </div>
            )}
            {tutors.map(t => {
              const isUnlocked = unlockedIds.includes(String(t._id));
              const initials = t.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div key={t._id} className="col-sm-6 col-xl-4">
                  <div className="card-hover" style={{ background: 'white', borderRadius: '20px', padding: '22px', boxShadow: 'var(--shadow-sm)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' }}>
                      <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: 'white', flexShrink: 0, fontFamily: 'var(--font-display)' }}>{initials}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '16px', marginBottom: '2px' }}>{t.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--gray)' }}>
                          <FiMapPin size={11} />{t.city}
                        </div>
                      </div>
                      {t.tutorProfile?.feePerHour && (
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--primary)' }}>₹{t.tutorProfile.feePerHour}/hr</span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {t.tutorProfile?.experience && (
                        <span style={{ padding: '3px 10px', background: '#FFF7ED', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: '#D97706' }}>⭐ {t.tutorProfile.experience} yrs exp</span>
                      )}
                      {t.tutorProfile?.teachingMode && (
                        <span style={{ padding: '3px 10px', background: modeColors[t.tutorProfile.teachingMode] || 'var(--light)', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: modeTextColors[t.tutorProfile.teachingMode] || 'var(--gray)' }}>
                          {t.tutorProfile.teachingMode}
                        </span>
                      )}
                      {t.tutorProfile?.qualification && (
                        <span style={{ padding: '3px 10px', background: 'var(--light)', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--dark3)' }}>{t.tutorProfile.qualification}</span>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px', flex: 1 }}>
                      {t.tutorProfile?.subjects?.map(s => (
                        <span key={s} style={{ padding: '3px 9px', background: 'var(--primary-light)', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--primary)' }}>{s}</span>
                      ))}
                    </div>

                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {isUnlocked ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiPhone size={14} color="var(--success)" /></div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)' }}>Contact</div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--success)' }}>{t.phone}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--gray)' }}>
                          <FiLock size={13} /> Contact hidden
                        </div>
                      )}
                      {!isUnlocked && (
                        <button onClick={() => setPayTarget(t)} className="btn-primary-custom" style={{ padding: '8px 16px', fontSize: '12px' }}>
                          Unlock ₹50
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {payTarget && (
        <PaymentModal
          targetUser={payTarget}
          onClose={() => setPayTarget(null)}
          onSuccess={() => { setPayTarget(null); fetchTutors(); toast.success('Contact unlocked!'); }}
        />
      )}
    </div>
  );
};

export default StudentDashboard;
